import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";

export default function AdminSettings() {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(settings ? { ...settings } : null);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInput = useRef(null);

  useEffect(() => {
    if (settings && !form) setForm({ ...settings });
  }, [settings, form]);

  if (!form) return <div className="text-[#6B6B6B]">Chargement...</div>;

  const updateZone = (i, field, value) => {
    const zones = [...form.delivery_zones];
    zones[i] = { ...zones[i], [field]: field === "fee" ? Number(value) : value };
    setForm({ ...form, delivery_zones: zones });
  };
  const updateZoneCities = (i, value) => {
    const zones = [...form.delivery_zones];
    zones[i] = { ...zones[i], cities: value.split(",").map((c) => c.trim()).filter(Boolean) };
    setForm({ ...form, delivery_zones: zones });
  };
  const addZone = () => setForm({ ...form, delivery_zones: [...form.delivery_zones, { name: "Nouvelle zone", fee: 0, cities: [] }] });
  const removeZone = (i) => setForm({ ...form, delivery_zones: form.delivery_zones.filter((_, idx) => idx !== i) });

  // Ticker messages
  const messages = form.ticker_messages || [];
  const setMessages = (arr) => setForm({ ...form, ticker_messages: arr });
  const addMsg = () => setMessages([...messages, ""]);
  const updateMsg = (i, v) => setMessages(messages.map((m, idx) => (idx === i ? v : m)));
  const removeMsg = (i) => setMessages(messages.filter((_, idx) => idx !== i));

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      toast.error("Veuillez sélectionner un fichier vidéo");
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error("Vidéo trop lourde (max 30 MB) — utilisez une vidéo courte");
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, intro_video_path: res.data.path });
      toast.success("Vidéo téléversée — n'oubliez pas d'enregistrer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingVideo(false);
      if (videoInput.current) videoInput.current.value = "";
    }
  };

  const removeVideo = () => setForm({ ...form, intro_video_path: null });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, ticker_messages: messages.filter((m) => m.trim().length > 0) };
      await api.put("/admin/settings", payload);
      toast.success("Paramètres mis à jour");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl lg:text-4xl mb-2">Paramètres</h1>
      <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mb-8">Configuration de la boutique</p>

      <div className="space-y-8" data-testid="settings-form">
        <section className="border border-black/5 bg-white p-6">
          <h3 className="font-display text-xl text-[#B8860B] mb-4">WhatsApp</h3>
          <label className="label-tiny block mb-1">Numéro WhatsApp (international sans +)</label>
          <input
            className="input-gold"
            value={form.whatsapp_number}
            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
            data-testid="settings-whatsapp"
          />
        </section>

        <section className="border border-black/5 bg-white p-6">
          <h3 className="font-display text-xl text-[#B8860B] mb-4">Vidéo d'accueil (max 30 MB)</h3>
          <p className="text-xs text-[#6B6B6B] mb-4 italic">
            Vidéo courte affichée en boucle silencieuse en haut de la page d'accueil (recommandé : 10 secondes).
          </p>
          {form.intro_video_path ? (
            <div className="space-y-3">
              <video
                src={fileUrl(form.intro_video_path)}
                className="w-full max-h-64 bg-black object-contain"
                muted
                autoPlay
                loop
                playsInline
              />
              <button
                type="button"
                onClick={removeVideo}
                className="text-xs uppercase tracking-widest text-red-600 hover:text-red-700"
                data-testid="remove-intro-video"
              >
                Retirer la vidéo
              </button>
            </div>
          ) : (
            <label className="border border-dashed border-black/20 aspect-video flex flex-col items-center justify-center cursor-pointer hover:border-[#B8860B]" data-testid="upload-intro-video">
              <Upload size={22} className="text-[#6B6B6B]" />
              <span className="text-xs uppercase tracking-widest text-[#6B6B6B] mt-2">
                {uploadingVideo ? "Envoi..." : "Cliquer pour choisir une vidéo"}
              </span>
              <input ref={videoInput} type="file" accept="video/*" onChange={uploadVideo} className="hidden" />
            </label>
          )}
        </section>

        <section className="border border-black/5 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-[#B8860B]">Bande défilante (ticker)</h3>
            <button type="button" onClick={addMsg} className="text-xs uppercase tracking-widest text-[#B8860B]" data-testid="add-ticker-msg">
              + Message
            </button>
          </div>
          <p className="text-xs text-[#6B6B6B] mb-4 italic">Messages qui défilent en haut de la page d'accueil.</p>
          <div className="space-y-2">
            {messages.length === 0 && (
              <p className="text-xs text-[#6B6B6B] italic">Aucun message pour le moment.</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_40px] gap-2">
                <input
                  className="input-gold"
                  value={m}
                  onChange={(e) => updateMsg(i, e.target.value)}
                  data-testid={`ticker-msg-${i}`}
                />
                <button type="button" onClick={() => removeMsg(i)} className="text-red-600 hover:text-red-700 flex items-center justify-center">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-black/5 bg-white p-6">
          <h3 className="font-display text-xl text-[#B8860B] mb-4">Messages prédéfinis</h3>

          <label className="label-tiny block mb-1">Message de précommande (rupture de stock)</label>
          <textarea className="input-gold min-h-[80px]" value={form.preorder_message} onChange={(e) => setForm({ ...form, preorder_message: e.target.value })} data-testid="settings-preorder-msg" />

          <label className="label-tiny block mb-1 mt-4">Message « Livré et notifié » (WhatsApp)</label>
          <textarea className="input-gold min-h-[140px]" value={form.delivered_message || ""} onChange={(e) => setForm({ ...form, delivered_message: e.target.value })} data-testid="settings-delivered-msg" />

          <label className="label-tiny block mb-1 mt-4">Message « Non livré » (WhatsApp)</label>
          <textarea className="input-gold min-h-[120px]" value={form.non_delivered_message || ""} onChange={(e) => setForm({ ...form, non_delivered_message: e.target.value })} data-testid="settings-non-delivered-msg" />

          <label className="label-tiny block mb-1 mt-4">Message de confirmation générique (ancien flux)</label>
          <textarea className="input-gold min-h-[80px]" value={form.confirmation_message} onChange={(e) => setForm({ ...form, confirmation_message: e.target.value })} data-testid="settings-confirm-msg" />
        </section>

        <section className="border border-black/5 bg-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-display text-xl text-[#B8860B]">Zones de livraison</h3>
            <button onClick={addZone} className="text-xs uppercase tracking-widest text-[#B8860B]" data-testid="add-zone-btn">+ Zone</button>
          </div>
          <div className="space-y-4">
            {form.delivery_zones.map((z, i) => (
              <div key={i} className="border border-black/10 p-4 space-y-2" data-testid={`zone-${i}`}>
                <div className="grid grid-cols-[1fr_140px_40px] gap-2">
                  <input className="input-gold" value={z.name} onChange={(e) => updateZone(i, "name", e.target.value)} placeholder="Nom" />
                  <input type="number" className="input-gold" value={z.fee} onChange={(e) => updateZone(i, "fee", e.target.value)} placeholder="Frais (FCFA)" />
                  <button onClick={() => removeZone(i)} className="text-red-600 hover:text-red-700"><X size={14} /></button>
                </div>
                <input
                  className="input-gold"
                  value={(z.cities || []).join(", ")}
                  onChange={(e) => updateZoneCities(i, e.target.value)}
                  placeholder="Villes séparées par des virgules"
                />
              </div>
            ))}
          </div>
          <label className="label-tiny block mb-1 mt-6">Frais zone libre (toute destination non répertoriée)</label>
          <input type="number" className="input-gold" value={form.free_zone_fee} onChange={(e) => setForm({ ...form, free_zone_fee: Number(e.target.value) })} />
        </section>

        <button className="btn-primary w-full" onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
        </button>
      </div>
    </div>
  );
}
